import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useData } from '../../context/DataContext';
import { callBillingAPI } from '../../utils/api';
import { SearchIcon, ChevronDownIcon, UserIcon, XIcon } from 'lucide-react';
interface ApiCustomer {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  organization?: string;
  terms_accepted: number | string;
}
interface CustomerItem {
  id: string;
  name: string;
  email: string;
  organization?: string;
}
interface CustomerSelectProps {
  value: string;
  onChange: (customerId: string) => void;
  error?: string;
  label?: string;
  /** Display name for a pre-selected customer (useful in edit mode) */
  selectedLabel?: string;
  /** Override company ID (defaults to selectedCompanyId from context) */
  companyId?: string;
  /** Disable the select (prevents opening dropdown) */
  disabled?: boolean;
}
export function CustomerSelect({
  value,
  onChange,
  error,
  label = 'Customer',
  selectedLabel,
  companyId,
  disabled = false
}: CustomerSelectProps) {
  const {
    selectedCompanyId
  } = useData();
  const effectiveCompanyId = companyId || selectedCompanyId;
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [customers, setCustomers] = useState<CustomerItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [displayName, setDisplayName] = useState(selectedLabel || '');
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  // Sync selectedLabel prop
  useEffect(() => {
    if (selectedLabel) {
      setDisplayName(selectedLabel);
    }
  }, [selectedLabel]);
  // Clear display name when value is cleared externally
  useEffect(() => {
    if (!value) {
      setDisplayName('');
    }
  }, [value]);
  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  // Fetch customers
  const fetchCustomers = useCallback(async (pageNum: number, search: string, append: boolean) => {
    if (!effectiveCompanyId) return;
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    try {
      const response = await callBillingAPI('getCustomers', {
        company_id: effectiveCompanyId,
        page: pageNum,
        search: search.trim()
      });
      const apiCustomers: ApiCustomer[] = response.items || [];
      const items: CustomerItem[] = apiCustomers.map(c => {
        const fullName = `${c.first_name} ${c.last_name}`.trim();
        const org = c.organization && c.organization.trim() !== fullName ? c.organization : '';
        return {
          id: c.id,
          name: fullName || c.email,
          email: c.email || '',
          organization: org
        };
      });
      if (append) {
        setCustomers(prev => [...prev, ...items]);
      } else {
        setCustomers(items);
      }
      setHasMore(response.has_next_page || false);
      setPage(pageNum);
      // If we have a value but no display name, try to find it
      if (value && !displayName && !append) {
        const match = items.find(c => c.id === value);
        if (match) {
          setDisplayName(match.name);
        }
      }
    } catch (err) {
      console.error('Failed to fetch customers:', err);
      if (!append) {
        setCustomers([]);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [effectiveCompanyId, value, displayName]);
  // Fetch on open or search change
  useEffect(() => {
    if (isOpen) {
      fetchCustomers(1, debouncedSearch, false);
    }
  }, [isOpen, debouncedSearch, effectiveCompanyId]);
  // Initial fetch to resolve display name if value is set
  useEffect(() => {
    if (value && !displayName && effectiveCompanyId) {
      fetchCustomers(1, '', false);
    }
  }, [value, effectiveCompanyId]);
  // Infinite scroll with IntersectionObserver
  useEffect(() => {
    if (!isOpen || !sentinelRef.current) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !isLoadingMore && !isLoading) {
        fetchCustomers(page + 1, debouncedSearch, true);
      }
    }, {
      root: listRef.current,
      threshold: 0.1
    });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [isOpen, hasMore, isLoadingMore, isLoading, page, debouncedSearch, fetchCustomers]);
  // Click outside to close
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);
  // Focus search input when opening
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isOpen]);
  const handleSelect = (customer: CustomerItem) => {
    onChange(customer.id);
    setDisplayName(customer.name);
    setIsOpen(false);
    setSearchQuery('');
  };
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setDisplayName('');
  };
  const handleToggle = () => {
    if (disabled) return;
    if (!isOpen) {
      setSearchQuery('');
      setCustomers([]);
      setPage(1);
      setHasMore(false);
    }
    setIsOpen(!isOpen);
  };
  return <div className="relative" ref={containerRef} data-id="element-79">
      {label && <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-80">
          {label}
        </label>}

      {/* Trigger button */}
      <button type="button" onClick={handleToggle} disabled={disabled} className={`w-full flex items-center gap-2 px-3 py-2.5 text-left bg-white border rounded-lg transition-colors text-sm ${disabled ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-70' : error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : isOpen ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-300 hover:border-gray-400'}`} data-id="element-81">
        {value && displayName ? <>
            <UserIcon className="w-4 h-4 text-gray-400 shrink-0" data-id="element-82" />
            <span className="flex-1 truncate text-gray-900" data-id="element-83">{displayName}</span>
            {!disabled && <button type="button" onClick={handleClear} className="p-0.5 rounded hover:bg-gray-100 transition-colors shrink-0" aria-label="Clear selection" data-id="element-84">
                <XIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-85" />
              </button>}
          </> : <>
            <UserIcon className="w-4 h-4 text-gray-400 shrink-0" data-id="element-86" />
            <span className="flex-1 text-gray-500" data-id="element-87">Select a customer</span>
            <ChevronDownIcon className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-88" />
          </>}
      </button>

      {error && <p className="mt-1 text-sm text-red-600" data-id="element-89">{error}</p>}

      {/* Dropdown panel */}
      {isOpen && <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-id="element-90">
          {/* Search input */}
          <div className="p-2 border-b border-gray-100" data-id="element-91">
            <div className="relative" data-id="element-92">
              <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-93" />
              <input ref={searchInputRef} type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search customers..." className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" data-id="element-94" />
            </div>
          </div>

          {/* Customer list */}
          <div ref={listRef} className="max-h-60 overflow-y-auto" data-id="element-95">
            {isLoading && customers.length === 0 ? <div className="py-8 text-center" data-id="element-96">
                <div className="inline-block w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mb-2" data-id="element-97"></div>
                <p className="text-xs text-gray-500" data-id="element-98">Loading customers...</p>
              </div> : customers.length === 0 ? <div className="py-8 text-center" data-id="element-99">
                <UserIcon className="w-6 h-6 text-gray-300 mx-auto mb-1" data-id="element-100" />
                <p className="text-xs text-gray-500" data-id="element-101">
                  {debouncedSearch ? 'No customers found' : 'No customers available'}
                </p>
              </div> : <>
                {customers.map(customer => <button key={customer.id} type="button" onClick={() => handleSelect(customer)} className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors ${customer.id === value ? 'bg-blue-50' : 'hover:bg-gray-50'}`} data-id="element-102">
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0" data-id="element-103">
                      <UserIcon className="w-4 h-4 text-gray-400" data-id="element-104" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-105">
                      <p className={`text-sm truncate ${customer.id === value ? 'font-semibold text-blue-900' : 'font-medium text-gray-900'}`} data-id="element-106">
                        {customer.name}
                      </p>
                      {customer.organization && <p className="text-xs text-gray-400 truncate" data-id="element-107">
                          {customer.organization}
                        </p>}
                      {customer.email && <p className="text-xs text-gray-500 truncate" data-id="element-108">
                          {customer.email}
                        </p>}
                    </div>
                    {customer.id === value && <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center shrink-0" data-id="element-109">
                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-110">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-111" />
                        </svg>
                      </div>}
                  </button>)}

                {/* Sentinel for infinite scroll */}
                <div ref={sentinelRef} className="h-1" data-id="element-112" />

                {isLoadingMore && <div className="py-3 text-center border-t border-gray-100" data-id="element-113">
                    <div className="inline-block w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-114"></div>
                  </div>}
              </>}
          </div>
        </div>}
    </div>;
}